import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@design-system';
import { AuthPageShell } from '@client/pages/SignIn';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import {
  clearIdleSignInEmail,
  clearIdleSignInRequest,
  clearIdleSignOutTimeout,
  parseIdleTimeoutMinutes,
  readIdleSignInEmail,
  readIdleSignInRequest,
  storeIdleSignInRequest,
} from './constants';

function useIdleSignInTransition(email: string | null): () => void {
  const navigate = useNavigate();
  const { isClerkLoading, isClerkSignedIn } = useClerkClientAuth();
  const [isSignInRequested, setIsSignInRequested] = useState(readIdleSignInRequest);

  useEffect(() => {
    if (!isSignInRequested || isClerkLoading || isClerkSignedIn) return;

    setIsSignInRequested(false);
    clearIdleSignInRequest();
    clearIdleSignInEmail();
    clearIdleSignOutTimeout();
    navigate('/', {
      replace: true,
      state: email ? { idleSignInEmail: email } : null,
    });
  }, [email, isClerkLoading, isClerkSignedIn, isSignInRequested, navigate]);

  return useCallback(() => {
    storeIdleSignInRequest();
    setIsSignInRequested(true);
  }, []);
}

export function IdleSessionEndedPage({
  timeoutMinutes: suppliedTimeoutMinutes,
}: {
  timeoutMinutes?: number;
}) {
  const [searchParams] = useSearchParams();
  const timeoutParam = searchParams.get('timeout');
  const timeoutMinutes =
    suppliedTimeoutMinutes ??
    (timeoutParam === null ? parseIdleTimeoutMinutes(null) : parseIdleTimeoutMinutes(timeoutParam));
  const email = readIdleSignInEmail();
  const returnToSignIn = useIdleSignInTransition(email);

  return (
    <AuthPageShell>
      <section className="flex w-full max-w-[446px] flex-col items-center gap-6 rounded-[10px] border bg-white px-8 pb-8 pt-10 text-center">
        <div className="bg-accent text-primary flex size-14 items-center justify-center rounded-full">
          <ShieldCheck className="size-7" />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold leading-7 text-gray-950">Your session has ended</h1>
          <p className="text-muted-foreground text-sm leading-5">
            For your security, we signed you out after {timeoutMinutes} minutes of inactivity.
            Please sign in again to continue.
          </p>
        </div>
        <Button
          className="h-11 w-full"
          onClick={returnToSignIn}
        >
          Sign in again
        </Button>
      </section>
    </AuthPageShell>
  );
}
